import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import {
  AuthState,
  LoginData,
  OAuthStatePayload,
  OidcErrorPayload,
  oidcFlowState,
  OidcLoginCompleteData,
  UserInfo,
  UserPermissions,
} from "./index.types";

// Enumeration of possible OIDC flow states

export const initialAuthStateValue: AuthState = {
  userId: null,
  isLoading: false,
  firstTimeLogin: false,
  onboardingSeen: {},
  isLogoutLoading: false,
  account_id: null,
  username: "",
  email: "",
  firstName: "",
  lastName: "",
  groups: [],
  photo: "",
  designation: "",
  phoneNumber: "",
  title: "",
  dob: "",
  loggedIn: false,
  message: "",
  userWasLoggedIn: false,
  checkLoggedInUser: false,
  isAuthorized: false,
  doLogout: false,
  isAuthInProgress: false,
  flowState: oidcFlowState.NOT_STARTED,
  oauthState: null,
  oidcNonce: null,
  oidcErrorDetail: "",
  userPermissions: {},
  isPermissionsLoaded: false,
};

export const authName = "auth";

export const loginSlice = createSlice({
  name: "auth",
  initialState: initialAuthStateValue,
  reducers: {
    login: (state) => {
      state.isLoading = true;
      state.isAuthInProgress = true;
      state.message = "";
      state.userWasLoggedIn = false;
    },
    getAccountsSuccess: (state, action: PayloadAction<UserInfo>) => {
      state.account_id = action.payload?.active_account;
      state.username = action.payload?.username;
      state.userId = action.payload?.id;
      state.email = action.payload?.email;
      state.firstName = action.payload?.first_name;
      state.lastName = action.payload?.last_name;
      state.groups = action.payload?.groups;
      state.photo = action.payload?.photo;
      state.designation = action.payload?.designation;
      state.phoneNumber = action.payload?.phone_number;
      state.title = action.payload?.title;
      state.dob = action.payload?.birthdate;
    },
    loginSuccess: (state) => {
      // Don't actually mark the user as logged in, just trigger a follow-on call
      // to get the current user, which will keep a similar flow for normal and
      // OIDC login.
      state.checkLoggedInUser = true;
    },
    loginFail: (state) => {
      state.isLoading = false;
      state.isAuthInProgress = false;
      state.loggedIn = false;
      state.message = "Username or password is wrong!";
    },
    logout: (state) => {
      state.isLogoutLoading = true;
    },
    logoutSuccess: () => {
      // Only clear CSRF token, all else is cleared by the root reducer
      Cookies.remove("csrftoken");
    },
    clearUserLoggedIn: (state) => {
      state.userWasLoggedIn = false;
    },
    logoutFail: (state) => {
      state.isLogoutLoading = false;
    },
    clearLoginMessage: (state) => {
      state.message = "";
      state.flowState = oidcFlowState.NOT_STARTED;
    },
    getCurrentUser: (state) => {
      state.loggedIn = false;
      state.isAuthInProgress = true;
      state.userWasLoggedIn = false;
      state.checkLoggedInUser = false;
    },
    getCurrentUserSuccess: (state, action: PayloadAction<UserInfo>) => {
      state.firstTimeLogin = action.payload?.first_login;
      state.onboardingSeen = action.payload?.onboarding_seen ?? {};
      state.loggedIn = true;
      state.userWasLoggedIn = true;
      state.account_id = action.payload?.active_account;
      state.username = action.payload?.username;
      state.userId = action.payload?.id;
      state.groups = action.payload?.groups;
      state.email = action.payload?.email;
      state.firstName = action.payload?.first_name;
      state.lastName = action.payload?.last_name;
      state.photo = action.payload?.photo;
      state.designation = action.payload?.designation;
      state.phoneNumber = action.payload?.phone_number;
      state.title = action.payload?.title;
      state.dob = action.payload?.birthdate;
    },
    // Mirrors the server-side stamps locally so remounting a cue's consumer does not
    // re-fire the mark-seen call before the next /users/current/ fetch.
    setOnboardingSeen: (state, action: PayloadAction<Record<string, string>>) => {
      state.onboardingSeen = action.payload;
    },
    getCurrentUserFail: (state) => {
      state.loggedIn = false;
      state.isLoading = false;
      state.isAuthInProgress = false;
      state.flowState = oidcFlowState.STARTED;
    },
    postOidcLoginInit: () => {},
    postOidcLoginInitSuccess: (state, action: PayloadAction<OAuthStatePayload>) => {
      state.flowState = oidcFlowState.INITIALIZED;
      state.oauthState = action.payload.state;
      state.oidcNonce = action.payload.nonce;
    },
    postOidcLoginInitFail: (state, action: PayloadAction<OidcErrorPayload>) => {
      state.flowState = oidcFlowState.ERROR;
      state.oidcErrorDetail = action.payload?.message || "";
    },
    postOidcLoginComplete: () => {},
    postOidcLoginCompleteSuccess: (state) => {
      state.flowState = oidcFlowState.COMPLETED;
    },
    postOidcLoginCompleteFail: (state, action: PayloadAction<OidcErrorPayload>) => {
      state.flowState = oidcFlowState.ERROR;
      state.oidcErrorDetail = action.payload?.message || "";
    },
    postOidcLogout: (state) => {
      state.isLogoutLoading = true;
    },
    postOidcLogoutSuccess: () => {
      // Only clear CSRF token, all else is cleared by the root reducer
      Cookies.remove("csrftoken");
    },
    postOidcLogoutFail: (state) => {
      state.isLogoutLoading = false;
    },
    getAccountsApi: (state) => {
      state.isLoading = true;
      state.isAuthInProgress = true;
      state.isAuthorized = false;
    },
    getAccountsApiFail: (state) => {
      state.isLoading = false;
      state.isAuthInProgress = false;
      state.isAuthorized = false;
    },
    getAccountsApiSuccess: (state) => {
      state.isAuthorized = true;
      state.isLoading = false;
      state.isAuthInProgress = false;
    },
    setFlowState: (state, action: PayloadAction<string>) => {
      state.flowState = action.payload;
    },
    setOidcError: (state, action: PayloadAction<string>) => {
      state.flowState = oidcFlowState.ERROR;
      state.oidcErrorDetail = action.payload;
    },
    getPermissionsSuccess: (state, action: PayloadAction<UserPermissions>) => {
      // Check if the returned permissions list is empty (as would happen if user
      // has no group membership, and set isAuthorized = false so Unauthorized
      // dialog displays rather than failing post-login API calls.
      if (Object.keys(action.payload).length === 0) {
        state.isAuthorized = false;
        state.isPermissionsLoaded = true;
      } else {
        state.userPermissions = action.payload;
        state.isPermissionsLoaded = true;
      }
    },
    getPermissionsFail: (state) => {
      state.userPermissions = {};
      state.isAuthorized = false;
    },
  },
});

export const {
  loginSuccess,
  loginFail,
  logout,
  logoutSuccess,
  clearLoginMessage,
  clearUserLoggedIn,
  setFlowState,
  setOidcError,
  setOnboardingSeen,
  postOidcLogoutSuccess,
} = loginSlice.actions;

export const selectUser = (state: any) => state.auth;

export default loginSlice.reducer;

export const onLogin = (data: LoginData, remainLogin: boolean = false) => ({
  payload: {
    apiName: authName,
    data,
    onSuccess: loginSuccess,
    method: "post",
    url: "/api/api-login",
    rememberMe: remainLogin,
    skipLogout: true,
  },
  type: "login",
});

export const getAccountID = () => ({
  payload: {
    apiName: authName,
    method: "get",
    url: `/api/users/current/`,
  },
  type: "getAccounts",
});

export const logoutApi = () => ({
  payload: {
    apiName: authName,
    method: "post",
    url: `/api/api-logout/`,
    skipLogout: true,
  },
  type: "logout",
});

export const getCurrentUser = () => ({
  payload: {
    apiName: authName,
    method: "get",
    url: `/api/users/current/`,
    skipLogout: true,
  },
  type: "getCurrentUser",
});

// API call to obtain a list of accounts available to the user, or will return 403
// if the user is not authorized to view any accounts. This is used in the login
// flow to determine if the user is authorized to use the application.
export const getAccountsApi = () => ({
  payload: {
    apiName: authName,
    method: "get",
    url: `/api/accounts/`,
    skipLogout: true,
  },
  type: "getAccountsApi",
});

export const postOidcLoginInit = () => ({
  payload: {
    apiName: authName,
    method: "post",
    url: `/api/oidc-login-api-init/`,
    skipLogout: true,
  },
  type: "postOidcLoginInit",
});

export const postOidcLoginComplete = (data: OidcLoginCompleteData) => ({
  payload: {
    apiName: authName,
    data,
    method: "post",
    url: `/api/oidc-login-api-complete/`,
    skipLogout: true,
  },
  type: "postOidcLoginComplete",
});

export const postOidcLogout = (data?: any) => ({
  payload: {
    apiName: authName,
    data,
    method: "post",
    url: `/api/oidc-logout-api/`,
    skipLogout: true,
  },
  type: "postOidcLogout",
});

// API call to obtain a list of permissions available to the user. This will be
// stored in Redux state and can be queried by various UI components to
// determine if the user has permission to perform a certain action.
export const getPermissions = () => ({
  payload: {
    apiName: authName,
    method: "get",
    url: `/api/permissions/`,
    skipLogout: true,
  },
  type: "getPermissions",
});
